import { useTranslation } from '@obvio/app'
import { NextSeo } from 'next-seo'

import { SITE_OG_IMAGE } from '@/constants/seo'
import { localizedUrl, toSiteLocale } from '@/utils/seo'

import type { ReactElement } from 'react'

type PageSeoProps = {
  /** Path without locale prefix, e.g. `pokoje/natura`. Empty for the home page. */
  path: string
  title?: string | undefined
  description?: string | undefined
  type?: 'website' | 'article'
  noindex?: boolean
}

const OG_LOCALES = { pl: 'pl_PL', en: 'en_GB' } as const

/**
 * Canonical URL pointing at the page in its own language, reciprocal hreflang
 * links (pl, en, x-default) and Open Graph data of the page itself, instead of
 * the site-wide defaults.
 */
export function PageSeo({
  path,
  title,
  description,
  type = 'website',
  noindex,
}: PageSeoProps): ReactElement {
  const { locale } = useTranslation()
  const current = toSiteLocale(locale)
  const canonical = localizedUrl(path, current)

  return (
    <NextSeo
      canonical={canonical}
      {...(title && { title })}
      {...(description && { description })}
      {...(noindex && { noindex: true, nofollow: true })}
      languageAlternates={[
        { hrefLang: 'pl', href: localizedUrl(path, 'pl') },
        { hrefLang: 'en', href: localizedUrl(path, 'en') },
        { hrefLang: 'x-default', href: localizedUrl(path, 'pl') },
      ]}
      openGraph={{
        type,
        url: canonical,
        locale: OG_LOCALES[current],
        ...(title && { title }),
        ...(description && { description }),
        images: [SITE_OG_IMAGE],
      }}
    />
  )
}
